import { useEffect, useRef, useState } from 'react'

const destinations = [
  {
    className: 'board-card-one',
    eyebrow: 'Portfolio',
    title: 'Edgezpace',
    description: 'Design, development, and selected work.',
    href: 'https://edgezpace.com/',
  },
  {
    className: 'board-card-three',
    eyebrow: 'Members',
    title: 'Member Apps',
    description: 'Private applications and tools for approved members.',
    href: 'https://accounts.zpacestudio.com/',
  },
]

export default function HeroDestinations() {
  const carouselRef = useRef(null)
  const hintTimersRef = useRef([])
  const interactionRef = useRef(false)
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const carousel = carouselRef.current
    const mobileQuery = window.matchMedia('(max-width: 640px)')
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    if (!carousel || !mobileQuery.matches || reducedMotionQuery.matches) {
      return undefined
    }

    const clearHint = () => {
      interactionRef.current = true
      hintTimersRef.current.forEach(window.clearTimeout)
      hintTimersRef.current = []
      carousel.classList.remove('is-peeking')
    }

    const beginHint = () => {
      const startTimer = window.setTimeout(() => {
        if (interactionRef.current) return
        carousel.classList.add('is-peeking')

        const finishTimer = window.setTimeout(() => {
          carousel.classList.remove('is-peeking')
        }, 620)
        hintTimersRef.current.push(finishTimer)
      }, 420)

      hintTimersRef.current.push(startTimer)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        observer.disconnect()
        beginHint()
      },
      { threshold: 0.45 },
    )

    observer.observe(carousel)
    carousel.addEventListener('pointerdown', clearHint, { passive: true })
    carousel.addEventListener('touchstart', clearHint, { passive: true })

    return () => {
      carousel.removeEventListener('pointerdown', clearHint)
      carousel.removeEventListener('touchstart', clearHint)
      observer.disconnect()
      hintTimersRef.current.forEach(window.clearTimeout)
      hintTimersRef.current = []
    }
  }, [])

  function updateActiveCard() {
    const carousel = carouselRef.current
    if (!carousel) return

    const cards = [...carousel.querySelectorAll('.board-card')]
    const carouselCenter = carousel.scrollLeft + carousel.clientWidth / 2
    const nextIndex = cards.reduce((closestIndex, card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2
      const closestCard = cards[closestIndex]
      const closestCenter = closestCard.offsetLeft + closestCard.offsetWidth / 2
      return Math.abs(cardCenter - carouselCenter) < Math.abs(closestCenter - carouselCenter)
        ? index
        : closestIndex
    }, 0)

    setActiveIndex(nextIndex)
  }

  function selectCard(index) {
    const carousel = carouselRef.current
    const card = carousel?.querySelectorAll('.board-card')[index]
    if (!carousel || !card) return

    interactionRef.current = true
    hintTimersRef.current.forEach(window.clearTimeout)
    hintTimersRef.current = []
    carousel.classList.remove('is-peeking')
    carousel.scrollTo({
      left: card.offsetLeft - (carousel.clientWidth - card.offsetWidth) / 2,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }

  return (
    <div className="hero-board" data-reveal="right" data-reveal-delay="0.08" aria-label="Zpace Studio destinations">
      <div className="orb orb-large" aria-hidden="true" />
      <div className="orb orb-small" aria-hidden="true" />
      <div className="orbit-line" aria-hidden="true" />

      <div ref={carouselRef} className="hero-carousel" onScroll={updateActiveCard}>
        {destinations.map((destination) => (
          <a
            className={`board-card ${destination.className}`}
            href={destination.href}
            key={destination.title}
          >
            <span className="mini-mark">{destination.eyebrow}</span>
            <strong>{destination.title}</strong>
            <span>{destination.description}</span>
          </a>
        ))}
      </div>

      <div className="hero-carousel-dots" aria-label="Choose a destination">
        {destinations.map((destination, index) => (
          <button
            className={index === activeIndex ? 'is-active' : ''}
            type="button"
            aria-label={`Show ${destination.title}`}
            aria-pressed={index === activeIndex}
            key={destination.title}
            onClick={() => selectCard(index)}
          />
        ))}
      </div>
    </div>
  )
}
